import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";

const IS_SAFARI = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/(Chrome|Chromium|CriOS|Android|Edg|OPR|Firefox)/i.test(ua);
})();

function isProbablyPdf({ url, mime }) {
  const m = (mime || "").toLowerCase();
  if (m.includes("pdf")) return true;
  try {
    const ext = (new URL(url, window.location.href).pathname.split(".").pop() || "").toLowerCase();
    return ext === "pdf";
  } catch {}
  return false;
}

function isSameOrigin(url) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return true; // blob:, data:
  }
}

export default function FilePreviewModal({ doc, onClose }) {
  const [imgError, setImgError] = useState(false);
  const isPdf = useMemo(() => (doc ? isProbablyPdf(doc) : false), [doc]);
  const sameOrigin = useMemo(() => (doc ? isSameOrigin(doc.url) : true), [doc]);

  const openInNewTab = () => window.open(doc?.url, "_blank", "noopener,noreferrer");

  return (
    <Dialog fullWidth maxWidth="lg" open={Boolean(doc)} onClose={onClose} scroll="paper">
      {doc && (
        <>
          <DialogTitle sx={{ p: 1.5 }} className="flex justify-between items-center">
            <span className="font-semibold truncate">{doc.name || "Anteprima"}</span>
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={openInNewTab}
                sx={{
                  color: "#6366f1",
                  borderColor: "#6366f1",
                  textTransform: "uppercase",
                  "&:hover": {
                    color: "#4f46e5",
                    borderColor: "#4f46e5",
                    backgroundColor: "rgba(99,102,241,0.06)",
                  },
                }}
              >
                Apri in nuova scheda
              </Button>
              <IconButton onClick={onClose}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 0,
              "& img, & embed, & object": { display: "block", maxWidth: "100%" },
            }}
          >
            {isPdf ? (
              IS_SAFARI ? (
                sameOrigin ? (
                  // Safari + same-origin: usa <embed>
                  <embed src={doc.url} type="application/pdf" width="100%" height={700} />
                ) : (
                  // Safari + cross-origin: PROVA comunque <object> e offri un messaggio di fallback sotto
                  <>
                    <object data={doc.url} type="application/pdf" width="100%" height={700}>
                      <Box sx={{ py: 3, px: 2 }}>
                        Impossibile mostrare l’anteprima del PDF.
                        &nbsp;
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 underline"
                        >
                          Apri / scarica il file
                        </a>
                      </Box>
                    </object>

                  </>
                )
              ) : (
                // Non Safari
                <object data={doc.url} type="application/pdf" width="100%" height={700}>
                  <Box sx={{ py: 3, px: 2 }}>
                    Impossibile mostrare l’anteprima del PDF.
                    &nbsp;
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline"
                    >
                      Apri / scarica il file
                    </a>
                  </Box>
                </object>
              )
            ) : (
              // Immagini / altri tipi
              <>
                {!imgError ? (
                  <img
                    src={doc.url}
                    alt={doc.name || "Immagine"}
                    height={700}
                    style={{ maxHeight: 700, width: "100%", objectFit: "contain" }}
                    onError={() => setImgError(true)}
                    loading="eager"
                  />
                ) : (
                  <Box sx={{ py: 3, px: 2 }}>
                    Impossibile mostrare l’anteprima.&nbsp;
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline"
                    >
                      Apri il file in una nuova scheda
                    </a>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
        </>
      )}
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 1,
          py: 1.5,
          px: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        {isPdf && IS_SAFARI && !sameOrigin && (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            Se non visualizzi il file,&nbsp;
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 underline"
            >
              aprilo in una nuova scheda
            </a>
            .
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}